import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Users, Flame, Gift, Megaphone, Palette, ShieldCheck, Building2, Instagram, HeartHandshake, ArrowDown, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { BrandedPhone } from "@/components/BrandedPhone";

export const CONTACT = "mailto:hello@mapetiteligue.com?subject=" + encodeURIComponent("Je veux une ligue à mon image");

export const EXAMPLES = [
  { id: "ex1", name: "Ligue de l'Agence", brand: { primary: "#E4572E", secondary: "#1B1B3A", button: "#E4572E", bg_mode: "color", bg_color: "#1B1B3A", tagline: "Les pronos du vendredi, entre collègues." } },
  { id: "ex2", name: "Fan Club Paris", brand: { primary: "#2EC4B6", secondary: "#011627", button: "#2EC4B6", bg_mode: "color", bg_color: "#011627", tagline: "La communauté qui vit chaque match." } },
  { id: "ex3", name: "Studio Nord", brand: { primary: "#FFBF00", secondary: "#2D2D2D", button: "#FFBF00", bg_mode: "color", bg_color: "#202020", tagline: "Une ligue pour toute l'équipe." } },
  { id: "ex4", name: "Les Passionnés", brand: { primary: "#9B5DE5", secondary: "#00BBF9", button: "#00BBF9", bg_mode: "color", bg_color: "#14102A", tagline: "Joue, commente, gagne des lots." } },
];

export const PERKS = [
  [Users, "Fais vivre ton équipe", "Chaque soir de match devient un rendez-vous : on pronostique, on se chambre, on en parle à la machine à café."],
  [Flame, "Crée une vraie dynamique", "Classements, défis entre membres, roi de la semaine, meilleur de la soirée : tout le monde a une raison de revenir."],
  [Gift, "Fais gagner des lots", "Mets en avant tes récompenses dans une vitrine dédiée : places de match, maillots, produits, avantages maison."],
  [Megaphone, "Anime ta communauté", "Plutôt qu'un énième post, propose un jeu où tes abonnés interagissent vraiment entre eux, toute la saison."],
  [Palette, "100 % à ton image", "Ton logo, tes couleurs, ton fond, ta photo d'équipe, tes e-mails et ta page d'invitation. L'app devient la tienne."],
  [ShieldCheck, "Simple et sans argent", "Aucun pari, aucune mise : un jeu amical et privé, sur invitation, que chacun rejoint en une minute."],
];

export const FOR = [[Building2, "Entreprises", "Team building, cohésion, séminaires, filiales."], [HeartHandshake, "Communautés & clubs", "Associations, fan clubs, clubs sportifs."], [Instagram, "Pages & créateurs", "Instagram, Facebook, X, TikTok, YouTube."], [Users, "Groupes d'amis XXL", "Grandes bandes, écoles, anciens élèves."]];

export const CUSTOM = ["Logo à la place de celui de l'app", "Couleurs, boutons et fond complet", "Photo d'équipe en bannière", "Vitrine des lots à gagner", "E-mails de rappel et récaps à tes couleurs", "Page d'invitation personnalisée", "Coche « ligue vérifiée »", "Jusqu'à 1 000 participants"];

function ContactButton({ testId, className = "", short }) {
  return <a href={CONTACT} className={`mpl-btn-primary whitespace-nowrap ${className}`} data-testid={testId}><Mail size={16} /> {short ? <><span className="sm:hidden">Contact</span><span className="hidden sm:inline">Contactez-nous</span></> : "Contactez-nous"}</a>;
}

export function Models({ items, eyebrow }) {
  const [list, setList] = useState(null);
  useEffect(() => { if (!items) api.get("/public/showcase").then(({ data }) => setList(data)).catch(() => setList([])); }, [items]);
  const models = items ? items.slice(0, 4) : [...(list || []), ...EXAMPLES].slice(0, 4);
  const ready = items || list !== null;
  return (
    <section id="modeles" className="relative z-10 mx-auto max-w-6xl scroll-mt-20 px-5 pb-20" data-testid="branded-models">
      <p className="mpl-eyebrow mb-2 text-[#B7F52A]">{eyebrow || (list && list.length ? "Ils ont déjà leur ligue" : "Exemples de ligues")}</p>
      <h2 className="mb-2 font-display text-3xl font-extrabold uppercase sm:text-4xl">Un aperçu, quatre univers</h2>
      <p className="mb-10 max-w-xl text-sm text-slate-400">Même jeu, même plaisir… mais chaque ligue porte les couleurs de celui qui l'organise. Chaque téléphone fait défiler les écrans de sa ligue (accueil, classement, pronos, vie de la ligue), avec des données fictives. Survole pour mettre en pause, ou choisis un écran avec les points.</p>
      {!ready ? <div className="h-[520px]" /> : (
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {models.map((m, i) => (
            <div key={m.id} className="mpl-rise" style={{ animationDelay: `${i * 90}ms` }}>
              <div className={i % 2 ? "lg:translate-y-8" : ""}><BrandedPhone m={m} screen={i} testId={`branded-model-${i}`} /></div>
              <p className="mt-2 text-center text-sm font-bold text-white lg:mt-10" data-testid={`branded-model-name-${i}`}>{m.name}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function BrandedLeagues() {
  useEffect(() => { document.title = "Ligue à ton image · Ma Petite Ligue"; window.scrollTo(0, 0); }, []);
  return (
    <div className="mpl-grain min-h-screen overflow-x-hidden bg-[#0B1020] text-white" data-testid="branded-page">
      <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between gap-3 px-5 py-5" style={{ paddingTop: "calc(1.25rem + env(safe-area-inset-top))" }}>
        <Link to="/" data-testid="branded-home-link"><Logo /></Link>
        <ContactButton testId="branded-header-contact" className="!px-4 text-sm" short />
      </header>
      <section className="relative z-10 mx-auto max-w-6xl overflow-hidden px-5 pb-20 pt-10 md:pt-20" data-testid="branded-hero">
        <div className="pointer-events-none absolute -right-20 top-0 h-80 w-80 rounded-full bg-[#B7F52A]/10 blur-3xl" aria-hidden="true" />
        <p className="mpl-eyebrow mpl-rise mb-4 text-[#B7F52A]">Entreprises · Communautés · Créateurs</p>
        <h1 className="mpl-rise max-w-4xl font-display text-5xl font-extrabold uppercase leading-[0.92] sm:text-6xl lg:text-7xl" style={{ animationDelay: "80ms" }} data-testid="branded-title">
          Une ligue NBA <span className="text-[#B7F52A]">à ton image.</span>
        </h1>
        <p className="mpl-rise mt-6 max-w-xl text-base text-slate-300" style={{ animationDelay: "160ms" }}>
          Ton logo, tes couleurs, tes lots : offre à ton équipe ou à ta communauté sa propre application de pronostics NBA. Un jeu fun qui fait vraiment interagir les gens, toute la saison.
        </p>
        <div className="mpl-rise mt-8 flex flex-wrap gap-3" style={{ animationDelay: "240ms" }}>
          <ContactButton testId="branded-hero-contact" />
          <a href="#modeles" className="mpl-btn-ghost" data-testid="branded-models-link">Voir les modèles <ArrowDown size={16} /></a>
        </div>
      </section>
      <Models />
      <section className="relative z-10 mx-auto max-w-6xl px-5 pb-20" data-testid="branded-perks">
        <p className="mpl-eyebrow mb-2 text-[#B7F52A]">Pourquoi une ligue à ton image</p>
        <h2 className="mb-10 max-w-2xl font-display text-3xl font-extrabold uppercase sm:text-4xl">Plus qu'un jeu : un rendez-vous qui rassemble</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PERKS.map(([I, t, d], i) => (
            <div key={t} className="mpl-card p-6 transition-transform duration-300 hover:-translate-y-1" data-testid={`branded-perk-${i}`}>
              <I className="mb-4 text-[#B7F52A]" size={22} />
              <p className="mb-2 font-bold text-white">{t}</p>
              <p className="text-sm text-slate-400">{d}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="relative z-10 mx-auto grid max-w-6xl gap-10 px-5 pb-20 md:grid-cols-2" data-testid="branded-for">
        <div>
          <p className="mpl-eyebrow mb-2 text-[#B7F52A]">Pour qui ?</p>
          <h2 className="mb-6 font-display text-3xl font-extrabold uppercase sm:text-4xl">Peu importe ton domaine</h2>
          <ul className="space-y-4">
            {FOR.map(([I, t, d]) => (
              <li key={t} className="flex gap-4"><span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-white/[0.05] text-[#B7F52A]"><I size={19} /></span>
                <span><span className="block font-bold text-white">{t}</span><span className="text-sm text-slate-400">{d}</span></span></li>
            ))}
          </ul>
        </div>
        <div className="mpl-card p-6" data-testid="branded-custom">
          <p className="mb-1 font-display text-2xl font-extrabold uppercase">Tout est sur mesure</p>
          <p className="mb-5 text-sm text-slate-400">On configure ta ligue avec toi, à ton image.</p>
          <ul className="grid gap-2.5 text-sm text-slate-200">
            {CUSTOM.map((c) => <li key={c} className="flex items-start gap-2"><Check size={16} className="mt-0.5 shrink-0 text-[#B7F52A]" /> {c}</li>)}
          </ul>
        </div>
      </section>
      <section className="relative z-10 mx-auto max-w-6xl px-5 pb-16" data-testid="branded-final">
        <div className="mpl-card flex flex-col items-start gap-5 border-[#B7F52A]/30 p-8 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="font-display text-3xl font-extrabold uppercase">Envie de ta propre ligue ?</p>
            <p className="mt-1 max-w-lg text-sm text-slate-400">Parle-nous de ton équipe ou de ta communauté : on te répond rapidement pour monter ta ligue à ton image.</p>
          </div>
          <ContactButton testId="branded-final-contact" className="shrink-0" />
        </div>
        <p className="mt-3 text-xs text-slate-500">Ou écris-nous directement : <a href={CONTACT} className="text-[#B7F52A] hover:underline" data-testid="branded-email-link">hello@mapetiteligue.com</a></p>
      </section>
      <footer className="relative z-10 mx-auto flex max-w-6xl flex-wrap gap-4 px-5 pb-10 text-xs text-slate-500">
        <span>© {new Date().getFullYear()} Ma Petite Ligue</span>
        <Link to="/" className="hover:text-white">Accueil</Link>
        <Link to="/confidentialite" className="hover:text-white">Confidentialité</Link>
        <span>Non affilié à la NBA.</span>
      </footer>
    </div>
  );
}
